// src/app/tools/adler32-hash/page.tsx
'use client';

import { useState, useCallback, useEffect } from 'react';
import { Sparkles, Copy, Check, X, Upload, FileText } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription, CardFooter } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { useToast } from '@/hooks/use-toast';
import { Toaster } from '@/components/ui/toaster';
import { ToolPageIntro } from '../../components/seo/ToolPageIntro';
import { InformationalContent } from '../../components/seo/InformationalContent';
import { adler32HashContent } from '../../data/adler32-hash-content';

type TabValue = 'text' | 'file';

interface FileInfo {
    name: string;
    size: number;
    type: string;
}

export default function Adler32HashGenerator() {
    const [activeTab, setActiveTab] = useState<TabValue>('text');
    const [input, setInput] = useState('');
    const [hash, setHash] = useState('');
    const [isCopied, setIsCopied] = useState(false);
    const [file, setFile] = useState<File | null>(null);
    const [fileInfo, setFileInfo] = useState<FileInfo | null>(null);
    const { toast } = useToast();

    // Adler-32 implementation
    const adler32 = (data: string): string => {
        let a = 1, b = 0;
        const MOD_ADLER = 65521;

        for (let i = 0; i < data.length; i++) {
            a = (a + data.charCodeAt(i)) % MOD_ADLER;
            b = (b + a) % MOD_ADLER;
        }

        return ((b << 16) | a).toString(16).toUpperCase().padStart(8, '0');
    };

    const generateHash = useCallback((text: string) => {
        if (!text.trim()) {
            setHash('');
            return;
        }
        setHash(adler32(text));
    }, []);

    const handleInputChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
        const value = e.target.value;
        setInput(value);
        generateHash(value);
    };

    const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const selectedFile = e.target.files?.[0];
        if (!selectedFile) return;

        if (selectedFile.size > 10 * 1024 * 1024) {
            toast({
                title: 'File too large',
                description: 'Maximum allowed size is 10MB',
            });
            return;
        }

        setFile(selectedFile);
        setFileInfo({
            name: selectedFile.name,
            size: selectedFile.size,
            type: selectedFile.type || 'Unknown',
        });
    };

    // Read file and generate hash
    useEffect(() => {
        if (!file) return;
        const reader = new FileReader();
        reader.onload = (event) => {
            const text = event.target?.result as string;
            generateHash(text);
        };
        reader.readAsText(file);
    }, [file, generateHash]);

    const handleRemoveFile = () => {
        setFile(null);
        setFileInfo(null);
        setInput('');
        setHash('');
    };

    const copyToClipboard = () => {
        if (!hash) return;
        navigator.clipboard.writeText(hash);
        setIsCopied(true);
        toast({
            title: 'Copied!',
            description: 'Hash has been copied to clipboard.',
        });
        setTimeout(() => setIsCopied(false), 2000);
    };

    const resetInput = () => {
        setInput('');
        setHash('');
        setFile(null);
        setFileInfo(null);
    };

    const handleUseSample = () => {
        const sample = 'This is a sample text for Adler-32 hashing.';
        setInput(sample);
        generateHash(sample);
    };

    // Drag-and-drop support
    const handleDrop = (e: React.DragEvent<HTMLDivElement>) => {
        e.preventDefault();
        if (e.dataTransfer.files && e.dataTransfer.files[0]) {
            const droppedFile = e.dataTransfer.files[0];
            if (droppedFile.size > 10 * 1024 * 1024) {
                toast({ title: 'File too large', description: 'Maximum allowed size is 10MB' });
                return;
            }
            setFile(droppedFile);
            setFileInfo({
                name: droppedFile.name,
                size: droppedFile.size,
                type: droppedFile.type || 'Unknown',
            });
        }
    };

    const handleDragOver = (e: React.DragEvent<HTMLDivElement>) => e.preventDefault();

    return (
        <div className="container mx-auto px-4 py-8">
            <div className="max-w-4xl mx-auto">
                <ToolPageIntro title="Adler32 Hash Generator">
                Generate Adler32 checksums from text or files. Fast, lightweight checksum useful for quick integrity checks. Paste or upload.
            </ToolPageIntro>

                <Card>
                    <CardHeader>
                        <CardTitle>Adler-32 Hash Generator</CardTitle>
                        <CardDescription>
                            Enter your text or upload a file to generate an Adler-32 checksum.
                        </CardDescription>
                    </CardHeader>

                    <CardContent>
                        <Tabs value={activeTab} onValueChange={(v) => setActiveTab(v as TabValue)} className="w-full">
                            <TabsList className="grid w-full grid-cols-2 mb-4">
                                <TabsTrigger value="text">Text</TabsTrigger>
                                <TabsTrigger value="file">File</TabsTrigger>
                            </TabsList>

                            <TabsContent value="text">
                                <div className="space-y-4">
                                    <div className="grid gap-2">
                                        <Label htmlFor="text-input">Input Text</Label>
                                        <Textarea
                                            id="text-input"
                                            value={input}
                                            onChange={handleInputChange}
                                            placeholder="Enter text to generate Adler-32 hash..."
                                            className="min-h-[200px]"
                                        />
                                    </div>
                                    <div className="flex flex-wrap gap-2">
                                        <Button variant="outline" size="sm" onClick={handleUseSample}>
                                            <Sparkles className="h-4 w-4 mr-2" /> Use Sample Text
                                        </Button>
                                        <Button variant="outline" size="sm" onClick={resetInput} disabled={!input && !file}>
                                            <X className="h-4 w-4 mr-2" /> Clear
                                        </Button>
                                    </div>
                                </div>
                            </TabsContent>

                            <TabsContent value="file">
                                <div
                                    className="space-y-4 border-2 border-dashed rounded-lg p-6 text-center"
                                    onDrop={handleDrop}
                                    onDragOver={handleDragOver}
                                >
                                    {fileInfo ? (
                                        <div className="border rounded-md p-4 flex items-center justify-between">
                                            <div className="flex items-center space-x-4">
                                                <FileText className="h-8 w-8 text-muted-foreground" />
                                                <div>
                                                    <p className="font-medium">{fileInfo.name}</p>
                                                    <p className="text-sm text-muted-foreground">
                                                        {Math.round(fileInfo.size / 1024)} KB · {fileInfo.type}
                                                    </p>
                                                </div>
                                            </div>
                                            <Button variant="ghost" size="icon" onClick={handleRemoveFile}>
                                                <X className="h-4 w-4" />
                                            </Button>
                                        </div>
                                    ) : (
                                        <div className="flex flex-col items-center justify-center space-y-2">
                                            <Upload className="h-8 w-8 text-muted-foreground" />
                                            <div className="text-sm text-muted-foreground">
                                                <label
                                                    htmlFor="file-upload"
                                                    className="relative cursor-pointer rounded-md font-medium text-primary hover:text-primary/90"
                                                >
                                                    <span>Upload a file</span>
                                                    <input
                                                        id="file-upload"
                                                        type="file"
                                                        className="sr-only"
                                                        onChange={handleFileChange}
                                                    />
                                                </label>
                                                <p className="pl-1">or drag and drop</p>
                                            </div>
                                            <p className="text-xs text-muted-foreground">Text files up to 10MB</p>
                                        </div>
                                    )}
                                </div>
                            </TabsContent>
                        </Tabs>

                        {/* Hash Output */}
                        <div className="mt-6">
                            <Label htmlFor="hash-output">Adler-32 Hash</Label>
                            <div className="flex items-center space-x-2 mt-1">
                                <Input
                                    id="hash-output"
                                    value={hash}
                                    readOnly
                                    placeholder="Hash will appear here..."
                                    className="font-mono"
                                />
                                <Button
                                    type="button"
                                    variant="outline"
                                    size="icon"
                                    onClick={copyToClipboard}
                                    disabled={!hash}
                                >
                                    {isCopied ? (
                                        <Check className="h-4 w-4 text-green-500" />
                                    ) : (
                                        <Copy className="h-4 w-4" />
                                    )}
                                </Button>

                            </div>
                        </div>
                    </CardContent>

                    <CardFooter className="flex flex-col items-start gap-2">
                        <div className="text-sm text-muted-foreground">
                            <p>
                                <strong>Adler-32</strong> is a checksum algorithm used for error-checking. It is not suitable for cryptographic purposes.
                            </p>
                        </div>
                    </CardFooter>
                </Card>

                {/* SEO Content */}
                <div className="mt-16">
                    <InformationalContent
                        title={adler32HashContent.title}
                        description={adler32HashContent.description}
                        content={adler32HashContent.content}
                        faqs={adler32HashContent.faqs}
                        relatedTools={adler32HashContent.relatedTools}
                    />
                </div>
            </div>

            <Toaster />
        </div>
    );
}
